import fetchJsonp from 'fetch-jsonp';
import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
import type { Country, Resort } from '@/shared/model';
import { adaptCountryResorts } from './adapter';
import type { ApiCountryResortsResponse } from './types';

const COUNTRY_RESORTS_TIMEOUT_MS = 20_000;

const inFlight = new Map<string, Promise<ApiCountryResortsResponse>>();

export function clearCountryResortsCache(): void {
  inFlight.clear();
}

function buildResortsUrl(base: string, countryId: number): string {
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}country=${encodeURIComponent(String(countryId))}`;
}

export function fetchCountryResorts(countryId: number, url?: string): Promise<ApiCountryResortsResponse> {
  // Справочник часть ключа: словари разных продуктов не делят промис
  const key = `${url ?? ''}::${countryId}`;
  const shared = inFlight.get(key);
  if (shared !== undefined) {
    return shared;
  }

  const request: Promise<ApiCountryResortsResponse> = requestCountryResorts(countryId, url).catch(() => {
    if (inFlight.get(key) === request) {
      inFlight.delete(key);
    }
    return [] as ApiCountryResortsResponse;
  });
  inFlight.set(key, request);
  return request;
}

async function requestCountryResorts(countryId: number, resortsUrl?: string): Promise<ApiCountryResortsResponse> {
  const config = useTravelataAppConfig();
  const url = buildResortsUrl(config.gatewayUrl + (resortsUrl || config.getResorts), countryId);

  const response = await fetchJsonp(url, { timeout: COUNTRY_RESORTS_TIMEOUT_MS });
  const body = await response.json<ApiCountryResortsResponse>();

  if (body.success !== true) {
    throw new Error('getCountryResorts: ответ не success');
  }
  return body;
}

export async function getCountryResorts(
  country?: Pick<Country, 'id' | 'name'> | undefined | null,
  url?: string,
): Promise<Resort[]> {
  if (!country) return [];
  const data = await fetchCountryResorts(country.id, url);
  return adaptCountryResorts(data.data || [], country);
}
